// components/CarPrinterPopup.tsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PrintContent from "./PrintContent";
import { useBusinessInfo } from "../hooks/useBusinessInfo";
import PrintConfigration, { PrintData } from "src/print-configuration/PrintConfigration";
import { splitDateTime } from "src/shared/components/dateTime";

interface CarPrinterPopupProps {
  orderData: any; // Mapped from Order or CarPayment details hook
  onClose: () => void;
}

const CarPrinterPopup: React.FC<CarPrinterPopupProps> = ({ orderData, onClose }) => {
  const { businessPrint, organizationData } = useBusinessInfo();
  const navigate = useNavigate();

  // Get customer name safely
  const getCustomerName = () => {
    const customer = orderData?.customerDetails;
    if (!customer) return "Walk-in Customer";

    const firstName = customer.firstName || "";
    const lastName = customer.lastName || "";
    const middleName = customer.middleName || "";

    const fullName = [firstName, middleName, lastName].filter(Boolean).join(" ");
    return fullName.trim() || "Walk-in Customer";
  };

  // Get total amount safely
  const getTotalAmount = () => {
    return orderData?.orders?.[0]?.finalAmount ?? orderData?.orders?.[0]?.orderAmount ?? orderData?.totalOutstandingBalance ?? 0;
  };

  const { date: orderDate, time: orderTime } = splitDateTime(
    orderData?.orderDate
      ? `${orderData.orderDate}T${orderData.orderTime || "00:00:00"}Z`
      : (orderData?.orderDetails?.[0]?.orderedOn?.$date ?? orderData?.orders?.[0]?.createdOn?.$date ?? new Date().toISOString()),
  );

  // Get products from the correct path in payload
  const rawProducts = orderData?.products || orderData?.orderDetails?.[0]?.products || [];

  // Get payment details
  const paymentHistory = orderData?.paymentHistory || orderData?.payments || [];
  const latestPayment = paymentHistory[paymentHistory.length - 1];

  // Calculate paid amount
  const paidAmount = orderData?.receivedAmount || latestPayment?.receivedAmount || paymentHistory.reduce((sum: number, p: any) => sum + (Number(p.receivedAmount) || 0), 0) || 0;

  const remainingAmount = orderData?.remainingAmount || orderData?.orders?.[0]?.remainingAmount || getTotalAmount() - paidAmount;

  const formatCars = () => {
    if (orderData?.serviceDetails?.length > 0) {
      return orderData.serviceDetails.map((service: any, index: number) => ({
        carNumber: service.carNumber || `CAR-${index + 1}`,
        services: [
          {
            productDetails:
              service.productDetails?.map((pd: any) => ({
                product: pd.productName || pd.name || "",
                price: Number(pd.price || pd.amount || pd.itemTotal || 0),
              })) || [],
          },
        ],
      }));
    }

    if (orderData?.cars?.length > 0) {
      return orderData.cars;
    }

    if (rawProducts.length > 0) {
      const carNumber = orderData?.carNumber || orderData?.vehicleNumber || orderData?.orders?.[0]?.carNumber || "Vehicle Services";

      return [
        {
          carNumber: carNumber,
          services: [
            {
              productDetails: rawProducts.map((p: any) => ({
                product: p.productName || p.name || "",
                price: Number(p.unitPrice || p.itemTotal || p.price || p.amount || 0),
                quantity: Number(p.quantity || 1),
              })),
            },
          ],
        },
      ];
    }

    return [];
  };

  // Format products for the print data
  const formattedProducts = rawProducts.map((p: any) => ({
    productName: p.productName || p.name || "",
    arabicword: p.arabicName || "",
    amount: Number(p.unitPrice || p.price || p.amount || 0),
    quantity: Number(p.quantity || 1),
    totalPrice: Number(p.lineTotal || p.itemTotal || Number(p.unitPrice || p.price || 0) * Number(p.quantity || 1) || 0),
  }));

  // Format payment history
  const formattedPaymentHistory = paymentHistory.map((p: any) => ({
    Paymentmethod: p.paymentMethod || "",
    ReceivedAmount: Number(p.receivedAmount) || 0,
    date: p.paidOn?.$date ? new Date(p.paidOn.$date).toLocaleDateString() : new Date().toLocaleDateString(),
    payment_on: p.paidOn?.$date ? new Date(p.paidOn.$date).toLocaleDateString() : new Date().toLocaleDateString(),
    balance: 0,
    paidAmount: Number(p.receivedAmount) || 0,
    balanceReturned: p.balanceReturned || 0,
  }));

  const printData: PrintData = {
    CustomerName: getCustomerName(),
    CustomerPhon: orderData?.customerDetails?.phones?.[0]?.phone || orderData?.phoneNumber || "",
    OrderId: orderData?.orderId || orderData?.orders?.[0]?.orderId || "N/A",
    OrderStatus: orderData?.orderStatus || orderData?.orders?.[0]?.status || "Confirm",
    discount: Number(orderData?.discount || orderData?.orders?.[0]?.discount || 0),
    Date: orderDate,
    Time: orderTime,
    businessType: "Carwash",
    Products: formattedProducts,
    Paid: Number(paidAmount),
    unpaid: Number(remainingAmount),
    OSB: Number(orderData?.totalOutstandingBalance || orderData?.outstandingBalance || 0),
    paymentMethod: latestPayment?.paymentMethod || orderData?.paymentMethod || "cash",
    subtotal: Number(getTotalAmount()),
    total: Number(orderData?.orderAmount || orderData?.orders?.[0]?.orderAmount || getTotalAmount()),
    billNo: orderData?.orders?.[0]?.billNumber || orderData?.billNo,
    deliveryFee: Number(orderData?.orders?.[0]?.deliveryFee || 0),
    payMentHistory: formattedPaymentHistory,
    payMentHistoryAvabile: formattedPaymentHistory.length > 0,
    clientId: orderData?.customerDetails?.clientId || orderData?.clientId,
  };

  const printDataWithCars = {
    ...printData,
    cars: formatCars(),
  };

  const handleCloseAfterPrint = (closed: boolean) => {
    if (!closed) {
      onClose();
      navigate("/v1/new-order");
    }
  };

  const customerName = getCustomerName();
  const totalAmount = getTotalAmount();

  return (
    <div className="text-center">
      <p className="text-gray-700 mb-2">Customer: {customerName}</p>
      <p className="text-gray-700 mb-6">Total Amount: {totalAmount}</p>

      <PrintConfigration BtnName="Print" PrintData={printDataWithCars as PrintData} className="ml-56" isClosed={handleCloseAfterPrint} />

      <div className="hidden print:block">
        <PrintContent orderData={orderData} orderId={orderData?.orderId || "N/A"} businessPrint={businessPrint} organizationData={organizationData} />
      </div>
    </div>
  );
};

export default CarPrinterPopup;
